import React from 'react';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface ValidatedInputProps {
  label: string;
  value: string | number | null | undefined;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  required?: boolean;
  type?: string;
  icon?: React.ReactNode;
  error?: string;
  isValid?: boolean;
}

export const ValidatedInput: React.FC<ValidatedInputProps> = ({
  label,
  value,
  onChange,
  placeholder,
  className,
  required = false,
  type = "text",
  icon,
  error,
  isValid
}) => {
  const isEmpty = !value || (typeof value === 'string' && !value.trim());
  const showError = required && isEmpty;
  const showSuccess = required && !isEmpty && isValid !== false;

  return (
    <div className="space-y-2">
      <Label className={cn(
        "flex items-center gap-1 text-sm font-medium",
        showError ? "text-red-600 dark:text-red-400" : "text-foreground"
      )}>
        {icon}
        {label}
        {required && <span className="text-red-500">*</span>}
      </Label>
      <div className="relative">
        <Input
          type={type}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={cn(
            className,
            showError && "border-red-300 dark:border-red-700 focus-visible:ring-red-500",
            showSuccess && "border-green-300 dark:border-green-700 focus-visible:ring-green-500"
          )}
        />
        {(showError || showSuccess) && (
          <div className="absolute right-2 top-1/2 -translate-y-1/2">
            {showError ? (
              <AlertCircle className="h-4 w-4 text-red-500" />
            ) : (
              <CheckCircle2 className="h-4 w-4 text-green-500" />
            )}
          </div>
        )}
      </div>
      {showError && error && (
        <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
          <div className="w-1 h-1 bg-red-500 rounded-full" />
          {error}
        </p>
      )}
    </div>
  );
};